import { useEffect, useState } from 'react';

import { FIGMA_TOKEN_KEY } from 'constants/token';

import * as api from 'api/methods';

import { getFigmaFileImages } from './helpers/getFigmaFileImages';

export const useFigmaFileImages = (fileId: string) => {
  const [figmaFileImages, setFigmaFileImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fileId) {
      setFigmaFileImages([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadFileImages = async () => {
      try {
        const fileImages = await getFigmaFileImages(fileId);

        if (!cancelled) {
          setFigmaFileImages(fileImages);
        }
      } catch (error) {
        console.warn(error);
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    setLoading(true);
    loadFileImages();

    return () => {
      cancelled = true;
    };
  }, [fileId]);

  return [loading, figmaFileImages] as const;
};

export const useFigmaFileMetadata = ({
  fileId,
  filePageId,
}: {
  fileId?: string;
  filePageId?: string;
}) => {
  const [loading, setLoading] = useState(Boolean(fileId));
  const [fileName, setFileName] = useState<string | null>(null);
  const [filePageName, setFilePageName] = useState<string | null>(null);
  const [haveToken, setHaveToken] = useState(
    () => !!localStorage.getItem(FIGMA_TOKEN_KEY),
  );

  useEffect(() => {
    if (!fileId) {
      setLoading(false);
      setFileName(null);
      setFilePageName(null);
      return;
    }

    let cancelled = false;

    const loadFileMetadata = async () => {
      setLoading(true);

      try {
        const file = await api.getFigmaFileMetadata(fileId);

        if (cancelled) {
          return;
        }

        const pageNode = file.document.children.find(
          (page: { id: string; name: string }) => page.id === filePageId,
        );

        setFileName(file.name);
        setFilePageName(pageNode?.name ?? null);
        setHaveToken(true);
      } catch (error) {
        console.warn(error);

        if (!cancelled) {
          localStorage.removeItem(FIGMA_TOKEN_KEY);
          setHaveToken(false);
          setFileName(null);
          setFilePageName(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadFileMetadata();

    return () => {
      cancelled = true;
    };
  }, [fileId, filePageId]);

  return {
    fileName,
    filePageName,
    haveToken,
    loading,
  };
};
